import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { Sparkle, Copy, Check } from '@phosphor-icons/react';
import { COPY_FEEDBACK_MS } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIComposer = ({ user }) => {
  const { t } = useTranslation();
  const [contentType, setContentType] = useState('headline');
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [copied, setCopied] = useState(false);

  const contentTypes = [
    { value: 'headline', label: t('composer.type_headline'), description: t('composer.type_headline_desc') },
    { value: 'vsl', label: t('composer.type_vsl'), description: t('composer.type_vsl_desc') },
    { value: 'email', label: t('composer.type_email'), description: t('composer.type_email_desc') },
    { value: 'ad', label: t('composer.type_ad'), description: t('composer.type_ad_desc') },
    { value: 'landing', label: t('composer.type_landing'), description: t('composer.type_landing_desc') },
  ];

  const handleGenerate = useCallback(async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setGeneratedContent('');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/generate`,
        {
          content_type: contentType,
          prompt: prompt.trim(),
          context: context.trim() || undefined
        },
        { withCredentials: true }
      );
      setGeneratedContent(response.data.generated_content);
    } catch {
      setGeneratedContent(t('composer.err_generate'));
    } finally {
      setLoading(false);
    }
  }, [contentType, prompt, context, t]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }, [generatedContent]);

  return (
    <DashboardLayout user={user} activePage="/ai-composer">
      <div className="p-6" data-testid="ai-composer-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
              <Sparkle size={28} weight="fill" className="text-[#2563EB]" />
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold tracking-tight">{t('composer.title')}</h1>
          </div>
          <p className="text-white/60">{t('composer.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <div className="space-y-6">
            {/* Content Type Selection */}
            <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
              <label className="block text-sm font-medium text-white/80 mb-3 uppercase tracking-[0.2em]">
                {t('composer.content_type')}
              </label>
              <div className="grid grid-cols-1 gap-2">
                {contentTypes.map((type) => (
                  <button
                    key={type.value}
                    data-testid={`content-type-${type.value}`}
                    onClick={() => setContentType(type.value)}
                    className={`p-4 rounded-lg text-left transition-all duration-200 ${
                      contentType === type.value
                        ? 'bg-[#2563EB] border-[#2563EB]'
                        : 'bg-[#0A0A0A] border-white/10 hover:bg-white/5'
                    } border`}
                  >
                    <p className="font-medium">{type.label}</p>
                    <p className="text-sm text-white/60 mt-1">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleGenerate} className="p-6 bg-[#121212] border border-white/10 rounded-xl">
              <div className="mb-4">
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('composer.what_create')}
                </label>
                <textarea
                  data-testid="prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={t('composer.prompt_ph')}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#2563EB] transition-colors resize-none"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('composer.context_label')}
                </label>
                <textarea
                  data-testid="context-input"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={t('composer.context_ph')}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#2563EB] transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="generate-btn"
                disabled={loading}
                className="w-full px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('composer.generating')}
                  </>
                ) : (
                  <>
                    <Sparkle size={20} weight="bold" />
                    {t('composer.generate')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Output Panel */}
          <div className="p-6 bg-[#121212] border border-white/10 rounded-xl h-fit lg:sticky lg:top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-[Outfit] font-bold">{t('composer.generated')}</h3>
              {generatedContent && (
                <button
                  onClick={handleCopy}
                  data-testid="copy-btn"
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
                >
                  {copied ? (
                    <>
                      <Check size={16} weight="bold" />
                      {t('composer.copied')}
                    </>
                  ) : (
                    <>
                      <Copy size={16} weight="bold" />
                      {t('composer.copy')}
                    </>
                  )}
                </button>
              )}
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-white/60">{t('composer.generating_your')}</p>
              </div>
            )}

            {!loading && generatedContent && (
              <div
                data-testid="generated-content"
                className="p-4 bg-[#0A0A0A] rounded-lg border border-white/10 min-h-[300px]"
              >
                <p className="text-white/90 whitespace-pre-wrap leading-relaxed">
                  {generatedContent}
                </p>
              </div>
            )}

            {!loading && !generatedContent && (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-[#2563EB]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Sparkle size={32} weight="bold" className="text-[#2563EB]" />
                </div>
                <p className="text-white/40">{t('composer.output_here')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AIComposer;